import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import TuneIcon from '@mui/icons-material/Tune';

export const parsePluginManifest = (pluginJson) => {
  try {
    const parsed = JSON.parse(pluginJson || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { manifest: null, error: new Error('Plugin manifest must be a JSON object.') };
    }
    return { manifest: parsed, error: null };
  } catch (error) {
    return { manifest: null, error };
  }
};

const formatValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const SummaryCard = ({ icon, title, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.75, sm: 2 },
      borderRadius: 2,
      borderColor: '#dbe2ea',
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          p: 0.75,
          borderRadius: 1.5,
          color: 'primary.main',
          backgroundColor: '#eef2ff',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Box>
    </Stack>
  </Paper>
);

const PluginManifestSummary = ({ pluginJson, t, onEditRaw }) => {
  const { manifest, error } = parsePluginManifest(pluginJson);

  if (error) {
    return (
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        <Alert severity="warning">
          {t(
            'global_setting.plugins_manifest_invalid_summary',
            'This plugin configuration cannot be displayed yet because the JSON is invalid.'
          )}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<CodeIcon />}
          onClick={onEditRaw}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('global_setting.plugins_edit_raw', 'View and edit raw JSON')}
        </Button>
      </Stack>
    );
  }

  const fields = Array.isArray(manifest.settings?.fields) ? manifest.settings.fields : [];
  const oauth = manifest.oauth && typeof manifest.oauth === 'object'
    ? manifest.oauth
    : null;
  const actionName = manifest.name || t('global_setting.plugins_action_fallback', 'This action');
  const description = String(manifest.description || '').trim();
  const connectionName = oauth?.label || oauth?.provider || t(
    'global_setting.plugins_connection_fallback',
    'Connected account'
  );

  return (
    <Stack spacing={2} sx={{ width: '100%', minHeight: 0, overflowY: 'auto', pr: 0.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('global_setting.plugins_configuration', 'Configuration')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'global_setting.plugins_configuration_description',
              'See what this action does and the options available to you. Technical details are available in raw JSON.'
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CodeIcon />}
          onClick={onEditRaw}
          sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {t('global_setting.plugins_edit_raw', 'View and edit raw JSON')}
        </Button>
      </Box>

      <SummaryCard
        icon={<InfoOutlinedIcon fontSize="small" />}
        title={t('global_setting.plugins_action_overview', 'What this action does')}
      >
        <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem' } }}>
          {actionName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {description || t(
            'global_setting.plugins_description_empty',
            'No description is available for this action.'
          )}
        </Typography>
      </SummaryCard>

      <SummaryCard
        icon={<TuneIcon fontSize="small" />}
        title={t('global_setting.plugins_settings', 'Options')}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          {t(
            'global_setting.plugins_settings_description',
            'These are the options you can use to customize this action.'
          )}
        </Typography>
        {fields.length ? (
          <Stack component="ul" spacing={1} sx={{ p: 0, m: 0, listStyle: 'none' }}>
            {fields.map((field, index) => {
              const fieldName = field?.label || field?.name || field?.key || t(
                'global_setting.plugins_option_fallback',
                'Option {{count}}',
                { count: index + 1 }
              );
              const fieldDescription = field?.description || field?.helperText;
              const hasDefault = field?.default !== undefined;
              const required = Boolean(field?.required);

              return (
                <Box
                  component="li"
                  key={`${field?.key || field?.label || 'field'}-${index}`}
                  sx={{
                    p: 1.25,
                    border: '1px solid #e2e8f0',
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'flex-start' }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {fieldName}
                      </Typography>
                      {fieldDescription ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {fieldDescription}
                        </Typography>
                      ) : null}
                      {hasDefault ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {t('global_setting.plugins_field_default', 'Default value')}: {formatValue(field.default)}
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip
                      size="small"
                      color={required ? 'primary' : 'default'}
                      variant={required ? 'filled' : 'outlined'}
                      label={required
                        ? t('global_setting.plugins_required', 'Required')
                        : t('global_setting.plugins_optional', 'Optional')}
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, flexShrink: 0 }}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('global_setting.plugins_settings_empty', 'This action does not require any options.')}
          </Typography>
        )}
      </SummaryCard>

      {oauth ? (
        <SummaryCard
          icon={<LinkIcon fontSize="small" />}
          title={t('global_setting.plugins_authentication', 'Connection')}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {connectionName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {oauth.accountRequired === false
              ? t(
                'global_setting.plugins_auth_optional_description',
                'A connected account can be used with this action.'
              )
              : t(
                'global_setting.plugins_auth_description',
                'This action needs a connected account to run.'
              )}
          </Typography>
        </SummaryCard>
      ) : null}
    </Stack>
  );
};

export default PluginManifestSummary;
